import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowRight, AlertCircle, FlaskConical, User as UserIcon, Mail } from 'lucide-react';

const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS === 'true';

const LoginPage = () => {
  const { login, devLogin, error, clearError, loading } = useAuth();
  const [devUsers, setDevUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [signingIn, setSigningIn] = useState(null);

  useEffect(() => {
    if (!DEV_BYPASS) return;
    let cancelled = false;
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const users = await authApi.listDevUsers();
        if (!cancelled) setDevUsers(users || []);
      } catch (err) {
        console.error('Failed to load dev users:', err);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    };
    fetchUsers();
    return () => { cancelled = true; };
    // Intentionally run once on mount; DEV_BYPASS and authApi are stable module-level refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDevLogin = async (email) => {
    if (!email) return;
    setSigningIn(email);
    const ok = await devLogin(email);
    setSigningIn(null);
    if (ok) {
      window.location.href = '/';
    }
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    if (customEmail.trim()) {
      handleDevLogin(customEmail.trim().toLowerCase());
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_15eae824-fd2f-440b-b0c8-e9a3a28c27b6/artifacts/19uczlft_GrowthHubElectric.png"
                alt="TMC Growth Hub"
                className="h-20 w-auto"
              />
              <h1 className="text-3xl font-semibold tracking-tight text-foreground">
                Welcome back
              </h1>
            </div>
            <p className="text-muted-foreground">
              {DEV_BYPASS
                ? 'Preview mode is active. Microsoft SSO is temporarily disabled — sign in as any user to explore the new Growth Hub redesign.'
                : 'Sign in with your Therapy Management SSO Single Sign On account to access your tactical.'}
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6 relative" data-testid="login-error">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
              <button
                onClick={clearError}
                className="absolute top-2 right-2 text-destructive-foreground/60 hover:text-destructive-foreground"
              >
                ×
              </button>
            </Alert>
          )}

          {DEV_BYPASS ? (
            <>
            <Card className="border-amber-200 shadow-sm" data-testid="dev-bypass-panel">
              <CardHeader className="pb-3 bg-amber-50/60 border-b border-amber-100 rounded-t-xl">
                <div className="flex items-center gap-2">
                  <FlaskConical className="w-4 h-4 text-amber-700" />
                  <CardTitle className="text-base text-amber-900">Preview Mode — Dev Sign-in</CardTitle>
                </div>
                <CardDescription className="text-amber-800/80 text-xs">
                  Click a user to sign in instantly, or enter any email to create a new preview account.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-5 space-y-5">
                {/* User list */}
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Existing users
                  </div>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="dev-users-list">
                    {loadingUsers && (
                      <div className="text-sm text-muted-foreground py-2">Loading users…</div>
                    )}
                    {!loadingUsers && devUsers.length === 0 && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900 space-y-2" data-testid="dev-users-empty">
                        <div className="font-semibold">This database has no users yet.</div>
                        <div className="text-amber-800/90 leading-relaxed">
                          You can either (1) enter any email below to create a preview account, or
                          (2) switch to <strong>Microsoft SSO</strong> using the button under the panel.
                          For a real production deployment, turn off <code className="px-1 py-0.5 bg-amber-100 rounded">DEV_AUTH_BYPASS</code> on the backend.
                        </div>
                      </div>
                    )}
                    {devUsers.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => handleDevLogin(u.email)}
                        disabled={!!signingIn}
                        data-testid={`dev-user-${u.email}`}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition text-left disabled:opacity-50"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center text-white text-xs font-semibold">
                          {(u.display_name || u.email || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">
                            {u.display_name || u.email}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                        </div>
                        {signingIn === u.email ? (
                          <span className="text-xs text-muted-foreground">Signing in…</span>
                        ) : (
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom email */}
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Sign in with any email
                  </div>
                  <form onSubmit={handleCustomSubmit} className="flex gap-2">
                    <div className="relative flex-1">
                      <Mail className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="email"
                        value={customEmail}
                        onChange={(e) => setCustomEmail(e.target.value)}
                        placeholder="name@therapymgmt.com"
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-slate-200 focus:outline-none focus:border-fuchsia-400 focus:ring-1 focus:ring-fuchsia-200"
                        data-testid="dev-custom-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={!customEmail.trim() || !!signingIn}
                      className="px-4"
                      data-testid="dev-custom-email-submit"
                    >
                      Sign in
                    </Button>
                  </form>
                </div>

                <div className="pt-2 border-t border-slate-100 text-[11px] text-muted-foreground leading-relaxed">
                  <strong className="text-amber-700">Preview-only.</strong> When you're ready to deploy,
                  set <code className="px-1 py-0.5 bg-slate-100 rounded">DEV_AUTH_BYPASS=false</code> in
                  the backend and <code className="px-1 py-0.5 bg-slate-100 rounded">REACT_APP_DEV_BYPASS=false</code> in
                  the frontend. Microsoft Entra ID will resume automatically.
                </div>
              </CardContent>
            </Card>

            {/* Escape hatch: Microsoft SSO is always available, even when DEV bypass is on.
                This matters for production deployments where the DB is empty and operators need the real SSO. */}
            <div className="mt-4 text-center">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                — or —
              </div>
              <Button
                onClick={login}
                disabled={loading}
                variant="outline"
                className="w-full h-11"
                data-testid="login-button-sso-fallback"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                </svg>
                Sign in with Microsoft instead
              </Button>
            </div>
            </>
          ) : (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Sign in</CardTitle>
                <CardDescription>
                  Use your @therapymgmt.com account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={login}
                  disabled={loading}
                  className="w-full h-12 btn-hover bg-primary hover:bg-primary/90"
                  data-testid="login-button"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                  </svg>
                  Sign in with Microsoft
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <div className="mt-6 text-center text-sm text-muted-foreground">
                  <p>Your session will remain active for 24 hours.</p>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="mt-8 text-center text-xs text-muted-foreground">
            <p>Therapy Management Corporation</p>
            <p className="mt-1">{DEV_BYPASS ? 'Preview / Development Build' : 'Protected by Microsoft Entra ID'}</p>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1619843968024-0672f9171f08?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjB3aGl0ZSUyMGJyaWdodHxlbnwwfHx8d2hpdGV8MTc2NjAxMTIxM3ww&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-primary/10" />
        <div className="absolute bottom-8 left-8 right-8">
          <Card className="bg-white/95 backdrop-blur border-0 shadow-lg">
            <CardContent className="p-6">
              <p className="text-sm font-medium text-foreground mb-2">
                "The Tactical system has transformed how we track and achieve our professional goals."
              </p>
              <p className="text-xs text-muted-foreground">
                — TMC Leadership Team
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
